import { AUTH_BASE } from 'api/constants';
import { jsonToGraphQLQuery, EnumType } from 'json-to-graphql-query';
import axios from 'axios';
import axiosObj, { axiosInstance as unauthAxios } from './config';
import { handleQuery } from '../utils/request';

const WORKSPACE_LOGIN = {
  workspaceSetupInvited: true,
  token: true,
  user: {
    id: true,
    email: true,
  },
  refreshToken: true,
}

const GOOGLE_LOGIN = {
  token: true,
  email: true,
  isEmailConfirmed: true,
  // workspaces: true,
  workspaceSetupInvited: true,
  passwordResetRequired: true,
}

const DEFAULT_USER_FIELDS = {
  id: true,
  firstName: true,
  lastName: true,
  status: true,
  tags: true,
  role: {
    id: true,
    name: true,
    usersCount: true,
    permissionUsers: true,
    permissionDevices: true,
    permissionOSUpdate: true,
    permissionGroups: true,
    permissionProfiles: true,
    permissionModules: true,
    permissionApplication: true,
    permissionRoles: true,
    permissionWorkspace: true,
    permissionBilling: true,
    permissionScopes: true,
    permissionDashboard: true,
    permissionDataset: true,
    permissionIncidents: true,
    permissionMonitoring: true,
    permissionReports: true,
    permissionLocations: true,
    permissionAiInsights: true,
    permissionDemoApp: true,
    permissionSideBarCustomSections: true,
  },
  address1: true,
  address2: true,
  city: true,
  country: true,
  postCode: true,
  phoneNumber: true,
  createdAt: true,
  updatedAt: true,
  language: true,
  email: true,
  lastLoginAt: true,
};

const DEFAULT_SSO_WORKSPACE_FIELDS = {
  id: true,
  name: true,
  organizationName: true,
  subDomain: true,
  logo: true,
  userRole: true,
  status: true,
  twoFactorEnabled: true,
  isNew: true,
  sso: {
    enforced: true,
    ssoType: true,
    passwordFallback: true,
    configuration: true
  },
};

const TWO_FACTOR_SETUP = {
  secret: true,
  recoveryKeys: true,
  qrCodeString: true,
};

const DEFAULT_ROLE_FIELDS = {
  id: true,
  name: true,
  description: true,
  tags: true,
  type: true,
  permissionDashboard: true,
  permissionProfiles: true,
  permissionDevices: true,
  permissionDataCollection: true,
  permissionImageAnnotation: true,
  permissionModelTraining: true,
  permissionDataset: true,
  permissionAIModel: true,
  permissionApplication: true,
  permissionModules: true,
  permissionUsers: true,
  permissionRoles: true,
  permissionWorkspace: true,
  permissionWorkspaceHistory: true,
  permissionBilling: true,
  permissionTerminalAccessManage: true,
  permissionVideoViewerManage: true,
  permissionScopes: true,
  permissionGroups: true,
  permissionOSUpdate: true,
  permissionIncidents: true,
  permissionMonitoring: true,
  permissionReports: true,
  permissionLocations: true,
  permissionAiInsights: true,
  permissionDemoApp: true,
  level: true,
  createdAt: true,
  updatedAt: true,
  usersCount: true,
  demoData: true,
};

export function getRoles(params) {
  const query = {
    query: {
      roles: {
        __args: params,
        ...DEFAULT_ROLE_FIELDS
      }
    }
  }
  return handleQuery(query, AUTH_BASE)
}

export function getAuthNotificationPermissions(params) {
  const query = {
    query: {
      notificationPermissions: {
        __args: params
      }
    }
  }
  return handleQuery(query, AUTH_BASE)
}

export function updateAuthNotificationPermissions(param) {
  const query = {
    mutation: {
      updateNotificationPermissions: {
        __args: param
      },
    },
  };

  return handleQuery(query, AUTH_BASE)
}

export function resetNotificationPermissions(roleId) {
  const query = {
    mutation: {
      resetNotificationPermissions: {
        __args: { id: roleId }
      },
    },
  };

  return handleQuery(query, AUTH_BASE)
}

export function unsubscribeUser(token) {
  const query = {
    mutation: {
      unsubscribeUser,
    },
  };

  return handleQuery(query, AUTH_BASE, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  })
}

export function getRoleTags(args) {
  const query = {
    query: {
      roleTags: {
        __args: args
      }
    }
  }
  return handleQuery(query, AUTH_BASE)
}

export function getUserTags(args) {
  const query = {
    query: {
      userTags: {
        __args: args
      }
    }
  }
  return handleQuery(query, AUTH_BASE)
}

export function updateRoleTags(args) {
  const query = {
    mutation: {
      bulkUpdateRoleTags: {
        __args: args,
      },
    },
  }
  return handleQuery(query, AUTH_BASE)
}

export function updateUserTags(args) {
  const query = {
    mutation: {
      bulkUpdateUserTags: {
        __args: args,
      },
    },
  }
  return handleQuery(query, AUTH_BASE)
}

export function obtainToken(data) {
  const loginQuery = {
    mutation: {
      login: {
        __args: {
          email: data.email,
          password: data.password,
          captchaToken: data.captchaToken,
        },
        token: true,
        email: true,
        workspaceSetupInvited: true,
        passwordResetRequired: true,
        isEmailConfirmed: true,
        workspaces: {
          id: true,
          logo: true,
          isNew: true,
          status: true,
          userRole: true,
          name: true,
          organizationName: true,
          subDomain: true,
          twoFactorEnabled: true,
          sso: {
            enforced: true,
            ssoType: true,
            configuration: true,
          },
          isFavourite: true,
        },
      },
    },
  };

  const gqlQuery = jsonToGraphQLQuery(loginQuery, { pretty: true });

  const config = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  };

  return unauthAxios(config);
}

export function workspaceLogin(data) {
  const loginQuery = {
    mutation: {
      workspaceLogin: {
        __args: {
          email: data.email,
          password: data.password,
          subDomain: data.subDomain,
        },
        workspaceSetupInvited: true,
        token: true,
        user: {
          id: true,
          email: true,
        },
      },
    },
  };

  const gqlQuery = jsonToGraphQLQuery(loginQuery, { pretty: true });

  const config = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  };

  return unauthAxios(config);
}

export function workspaceSelect(data) {
  const selectWorkspace = {
    mutation: {
      selectWorkspace: {
        __args: data,
        ...WORKSPACE_LOGIN
      }
    }
  }

  const gqlQuery = jsonToGraphQLQuery(selectWorkspace, { pretty: true });

  const config = {
    method: 'POST',
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  };

  return axiosObj(config);
}

export function updateWorkspaceFavourite(data) {
  const updateWorkspaceFavourite = {
    mutation: {
      updateWorkspaceFavourite: {
        __args: data
      }
    }
  }

  const gqlQuery = jsonToGraphQLQuery(updateWorkspaceFavourite, { pretty: true });

  const config = {
    method: 'POST',
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  };

  return axiosObj(config);
}

export function selectWorkspaceBySSO(data) {
  const selectWorkspaceBySSO = {
    mutation: {
      selectWorkspaceBySSO: {
        ...WORKSPACE_LOGIN,
        __args: data
      }
    }
  }

  const gqlQuery = jsonToGraphQLQuery(selectWorkspaceBySSO, { pretty: true });

  const config = {
    method: 'POST',
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  };

  return axiosObj(config);
}

// export function selectWorkspaceBySSO(args) {
//   const query = {
//     mutation: {
//       selectWorkspaceBySSO: {
//         __args: args,
//         ...WORKSPACE_LOGIN
//       },
//     },
//   }
//   return handleQuery(query, AUTH_BASE)
// }

export function testSSOConnection(args) {
  const query = {
    mutation: {
      testSSOConnection: {
        __args: args
      },
    },
  }
  return handleQuery(query, AUTH_BASE)
}

export function updatePasswordPolicies(args) {
  const query = {
    mutation: {
      updatePasswordPolicies: {
        __args: args
      },
    },
  }
  return handleQuery(query, AUTH_BASE)
}

export function updateSSOSetup(args) {
  const query = {
    mutation: {
      updateSSOSetup: {
        __args: args
      },
    },
  }
  return handleQuery(query, AUTH_BASE)
}

export function sendResetPassword(param) {
  const query = {
    mutation: {
      sendResetPassword: {
        __args: param,
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return unauthAxios({
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function resetPassword(param) {
  const query = {
    mutation: {
      resetPassword: {
        __args: param,
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  const config = {
    method: 'POST',
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  };

  return axiosObj(config);
}

export function register(param) {
  const query = {
    mutation: {
      signup: {
        __args: param,
        token: true,
        email: true,
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return unauthAxios({
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function invitationSignup(param) {
  const query = {
    mutation: {
      invitationSignup: {
        __args: param,
        token: true,
        user: { ...DEFAULT_USER_FIELDS },
      },
    },
  };
  return handleQuery(query, AUTH_BASE)
}

export function twoFactorRecoveryKeys() {
  const query = {
    query: {
      twoFactorRecoveryKeys
    }
  };

  return handleQuery(query, AUTH_BASE)
}

export function enableTwoFactor(param) {
  const query = {
    mutation: {
      enableTwoFactor: {
        __args: param,
      },
    },
  };

  return handleQuery(query, AUTH_BASE)
}

export function initTwoFactorSetup() {
  const query = {
    mutation: {
      initTwoFactorSetup: {
        ...TWO_FACTOR_SETUP,
      },
    },
  };

  return handleQuery(query, AUTH_BASE)
}


export function disableTwoFactor() {
  const query = {
    mutation: {
      disableTwoFactor
    },
  };

  return handleQuery(query, AUTH_BASE)
}

export async function getNRStatus(workspaceId) {
  const API_URL = process.env.API_URL.trim();
  let env = 'dev';

  if (API_URL.includes('staging')) {
    env = 'staging'
  }

  if (API_URL.includes('app')) {
    env = 'app'
  }

  if (API_URL.includes('dev')) {
    env = 'dev'
  }

  const result = await axios.get(`https://builder-${workspaceId}.${env}.viso.ai`);

  return result;
}

export async function getFiftyoneStatus(workspaceId) {
  const API_URL = process.env.API_URL.trim();
  let env = 'dev';

  if (API_URL.includes('staging')) {
    env = 'staging'
  }

  if (API_URL.includes('app')) {
    env = 'app'
  }

  if (API_URL.includes('dev')) {
    env = 'dev'
  }

  const result = await axios.get(`https://dataset-${workspaceId}.${env}.viso.ai/`);

  return result;
}

export async function googleLogin(args) {
  const query = {
    mutation: {
      googleLogin: {
        // ...GOOGLE_LOGIN,
        ...WORKSPACE_LOGIN,
        __args: args
      },
    },
  };

  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  const config = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  };

  return unauthAxios(config);
}

export function loginSso(param) {
  const args = {
    code: param.code,
    ssoType: new EnumType(param.ssoType || 'google'),
  };
  if (param.codeVerifier) {
    args.codeVerifier = param.codeVerifier;
  }

  const query = {
    mutation: {
      loginSso: {
        __args: args,
        token: true,
        email: true,
        isEmailConfirmed: true,
        workspaceSetupInvited: true,
        passwordResetRequired: true,
        workspaces: { ...DEFAULT_SSO_WORKSPACE_FIELDS },
      },
    },
  };

  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  const config = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-frontend-origin': window.location.origin,
    },
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  };

  return unauthAxios(config);
}